import React, {useCallback} from 'react';
import {generateRoutePath, getLocale} from '@epic-core/common';
import {extractSlugLocale, withLocalization} from '@epic-mw/localization';

import {EpicGamesNav, AccountLinks, NavLinkProps, defaultSignInLink, defaultSignOutLink, EpicGamesNavProps, Header} from 'epic-games-nav/dist/client';
import {useAddOnClick, AddOnClickFn, getHrefWithLocale} from 'epic-games-nav-shared';
import {useAccountStore} from 'epic-ue-shared';
import {MessageContext, LocalizedMessages, SupportedLocale} from '@epic-core/config/src/types';
import styled from 'styled-components';
import {getNavTheme} from './nav-theme';
import {useLocation, useNavigate} from 'react-router-dom';
import root from 'window-or-global';

const originEos = /(localhost.epicgames-gamedev.ol.epicgames.net|dev.epicgames-gamedev.ol.epicgames.net|dev.epicgames.com|dev-epicgames-gamedev.marketing.dev.on.epicgames.com)/g;
const invalidPaths = /^\/(docs|community|portal)/;

const Styled = styled.div`
    ${Header} {
        .propLogo,
        .shieldLogo {
            opacity: 1;
            &:hover {
                opacity: 0.7;
            }
        }
        .h4 {
            opacity: 0.75;
            letter-spacing: 2px;
            font-size: 11px;
        }
    }
`;

/**
 * Prepares the array of site nav links for the header
 * @param {array} links
 */
export function setLinks(links: NavLinkProps[], addOnClick: AddOnClickFn, pathname: string): NavLinkProps[] {
    if (!links) return [];

    const locale = getLocale();
    const {pageSlug} = extractSlugLocale(pathname, locale);

    for (let i = 0; i < links.length; i++) {
        let link = links[i];

        if (link.key === 'eos' && originEos.test(root.location.origin) && !invalidPaths.test(pageSlug)) {
            link.active = true;
        }

        let external = true;
        if (link.useCustomCallback) {
            external = false;
            const {localeHref} = getHrefWithLocale({...link, locale: getLocale()});
            const linkPath = generateRoutePath(localeHref || '');

            let active = false;
            if (localeHref && pathname.indexOf(linkPath) > -1) {
                active = true;
            } else {
                active = false;
            }
            link.active = active;
        }
        link = addOnClick(link, external);
        // We must go deeper!
        if (link.kids) {
            link.kids = setLinks(link.kids, addOnClick, pathname);
        }
        if (link.enhancedDropdown && link.enhancedDropdown.externalColumn) {
            link.enhancedDropdown.externalColumn.links = setLinks(link.enhancedDropdown.externalColumn.links || [], addOnClick, pathname);
        }
    }

    return links;
}

export function setAccountLinks(accountLinks: AccountLinks): AccountLinks {
    const signInLinks = accountLinks.signInLinks || [];

    signInLinks.forEach(signInLink => {
        if (signInLink.key === 'signin') {
            const {href: signInHref} = defaultSignInLink();
            signInLink.href = signInHref;
            signInLink.target = '_self';
        }
    });

    const signOutLink = defaultSignOutLink();
    if (accountLinks.signOutLink) {
        accountLinks.signOutLink.href = signOutLink.href;
    } else {
        accountLinks.signOutLink = signOutLink;
    }

    accountLinks.signOutLink.target = '_self';

    return accountLinks;
}

export function setCTA(callToAction: NavLinkProps, addOnClick: AddOnClickFn): NavLinkProps {
    let href = callToAction.href;
    if (callToAction.useCustomCallback) {
        href = generateRoutePath(`/download`);
    }

    const link = {
        ...callToAction,
        href
    };

    return addOnClick(link);
}

interface Properties {
    supportedLocales?: SupportedLocale[];
    getMessage?: (key: string) => string;
    messages?: MessageContext;
    config: EpicGamesNavProps;
}

const NavWrap = (props: Properties): JSX.Element => {
    const config = props.config;
    const location = useLocation();
    const messages = props.messages as LocalizedMessages;
    const pathname = location.pathname;
    const {account} = useAccountStore();
    const navTheme = getNavTheme();

    const supportedLocales = props.supportedLocales;

    const locale = getLocale();
    const navigate = useNavigate();
    const pushable = {
        push: navigate
    };
    const addOnClick = useAddOnClick(pushable, locale);
    const links = setLinks(config.links || [], addOnClick, pathname);
    const logoLink = addOnClick(config.logoLink || {});
    const callToAction = setCTA(config.callToAction || {}, addOnClick);

    const localeOptions = supportedLocales;

    const onSearch = useCallback(
        query => {
            navigate(generateRoutePath(`/search?keyword=${query}`));
        },
        [navigate]
    );

    const accountLinks = setAccountLinks(config.accountLinks || {});

    return (
        <Styled>
            <EpicGamesNav
                {...config}
                i18n={{
                    locale,
                    messages
                }}
                theme={navTheme}
                links={links}
                isLoggedIn={account.isLoggedIn}
                displayName={account.displayName}
                hideCallToAction={config.hideCallToAction || !callToAction}
                callToAction={callToAction}
                accountLinks={accountLinks}
                logoLink={logoLink}
                localeOptions={localeOptions}
                locale={locale}
                onSearch={onSearch}
                collapseTargetMaxWidth={1500}
            />
        </Styled>
    );
};

export const NavWrapper = withLocalization(NavWrap);
